import apiClient from '../utils/apiClient';

/**
 * Attendance API Service
 * Quản lý điểm danh check-in/check-out
 */
class AttendanceAPI {
  /**
   * Lấy danh sách điểm danh
   * @param {Object} params - Tham số tìm kiếm
   * @param {string} params.branch_id - ID chi nhánh
   * @param {string} params.employee_id - ID nhân viên
   * @param {string} params.date - Ngày (YYYY-MM-DD)
   * @param {string} params.start_date - Ngày bắt đầu
   * @param {string} params.end_date - Ngày kết thúc
   * @param {number} params.page - Trang
   * @param {number} params.limit - Số lượng mỗi trang
   * @returns {Promise} Danh sách điểm danh
   */
  getAttendances(params = {}) {
    return apiClient.get({
      url: '/attendances',
      params,
    });
  }

  /**
   * Lấy chi tiết điểm danh
   * @param {string} id - ID điểm danh
   * @returns {Promise} Chi tiết điểm danh
   */
  getAttendance(id) {
    return apiClient.get({
      url: `/attendances/${id}`,
    });
  }

  /**
   * Check-in cho ca làm việc
   * @param {Object} data - Dữ liệu check-in
   * @param {string} data.shift_id - ID ca làm việc
   * @param {string} data.check_in_ip - IP check-in
   * @param {Object} data.check_in_location - Vị trí GPS
   * @param {string} data.check_in_method - Phương thức check-in
   * @returns {Promise} Kết quả check-in
   */
  checkIn(data) {
    return apiClient.post({
      url: '/attendances/check-in',
      data,
    });
  }

  /**
   * Check-out cho ca làm việc
   * @param {Object} data - Dữ liệu check-out
   * @param {string} data.shift_id - ID ca làm việc
   * @param {string} data.check_out_ip - IP check-out
   * @param {Object} data.check_out_location - Vị trí GPS
   * @param {string} data.notes - Ghi chú
   * @returns {Promise} Kết quả check-out
   */
  checkOut(data) {
    return apiClient.post({
      url: '/attendances/check-out',
      data,
    });
  }

  /**
   * Admin/Manager đóng ca thay
   * @param {string} shiftId
   * @param {Object} data
   */
  forceCheckOut(shiftId, data) {
    return apiClient.post({
      url: `/attendance/force-check-out/${shiftId}`,
      data,
    });
  }

  /**
   * Cập nhật điểm danh thủ công (admin)
   * @param {string} id - ID điểm danh
   * @param {Object} data - Dữ liệu cập nhật
   * @param {string} data.check_in_time - Thời gian check-in
   * @param {string} data.check_out_time - Thời gian check-out
   * @param {string} data.notes - Ghi chú
   * @returns {Promise} Kết quả cập nhật
   */
  updateAttendance(id, data) {
    return apiClient.put({
      url: `/attendances/${id}`,
      data,
    });
  }

  /**
   * Lấy thống kê điểm danh theo khoảng thời gian
   * @param {Object} params - Tham số thống kê
   * @param {string} params.branch_id - ID chi nhánh
   * @param {string} params.start_date - Ngày bắt đầu
   * @param {string} params.end_date - Ngày kết thúc
   * @param {string} params.group_by - Nhóm theo (day, week, month)
   * @returns {Promise} Thống kê điểm danh
   */
  getAttendanceStats(params = {}) {
    return apiClient.get({
      url: '/attendances/stats',
      params,
    });
  }

  /**
   * Lấy báo cáo điểm danh
   * @param {Object} params - Tham số báo cáo
   * @param {string} params.branch_id - ID chi nhánh
   * @param {string} params.month - Tháng (YYYY-MM)
   * @param {string} params.employee_id - ID nhân viên
   * @returns {Promise} Báo cáo điểm danh
   */
  getAttendanceReport(params = {}) {
    return apiClient.get({
      url: '/attendances/report',
      params,
    });
  }

  /**
   * #210314 — Xoá dữ liệu khuôn mặt của nhân viên để họ đăng ký lại từ đầu.
   * Đồng thời ĐÓNG sự cố "lỗi đăng ký khuôn mặt" đang mở ở phía máy chủ.
   * @param {string} employeeId - ID nhân viên
   * @returns {Promise}
   */
  resetFaceEnrollment(employeeId) {
    return apiClient.post({
      url: `/attendance/face-enroll-reset/${employeeId}`,
      data: {},
    });
  }

  /**
   * #210314 — Miễn (hoặc gỡ miễn) đăng ký khuôn mặt cho nhân viên.
   * Dùng khi máy của nhân viên không sao nhận diện được — đây là đường kết thúc sự cố để
   * nhóm "Báo cáo sự cố" ngừng nhận cảnh báo lặp.
   * @param {string} employeeId - ID nhân viên
   * @param {boolean} exempt - true = miễn, false = gỡ miễn
   * @param {string} reason - Lý do (bắt buộc khi miễn)
   * @returns {Promise}
   */
  setFaceEnrollExemption(employeeId, exempt, reason) {
    return apiClient.post({
      url: `/attendance/face-enroll-exempt/${employeeId}`,
      data: { exempt, reason },
    });
  }
}

export const attendanceAPI = new AttendanceAPI();
export default attendanceAPI;
